import { useState, useEffect } from 'react';
import { getUser, getEnvironment, getGeography, getAccessToken } from '@access-evo/react-app-api';
import { isEvoPlatformAvailable, isIframeMode } from '../lib/environmentUtils';

interface EvoUser {
    id: string;
    emailAddress: string;
    organisation: { id: string };
}

interface EvoContextState {
    user: EvoUser | null;
    environment: string | null;
    geography: string | null;
    loading: boolean;
    isEvoContext: boolean;
}

export function useEvoContext(): EvoContextState {
    const [state, setState] = useState<EvoContextState>({
        user: null,
        environment: null,
        geography: null,
        loading: true,
        isEvoContext: false,
    });

    /* eslint-disable react-hooks/set-state-in-effect -- intentional sync of iframe detection state */
    useEffect(() => {
        if (!isEvoPlatformAvailable()) {
            setState(prev => ({ ...prev, loading: false, isEvoContext: false }));
            return;
        }

        const loadContext = async () => {
            try {
                const [user, environment, geography] = await Promise.all([
                    getUser(),
                    getEnvironment(),
                    getGeography(),
                ]);

                setState({
                    user: user as EvoUser,
                    environment,
                    geography,
                    loading: false,
                    isEvoContext: true,
                });
            } catch (err) {
                console.debug('ProjectApproval_ Failed to load Evo context:', err);
                setState(prev => ({ ...prev, loading: false, isEvoContext: false }));
            }
        };

        loadContext();
    }, []);

    return state;
}

const MAX_TOKEN_RETRIES = 5;
const RETRY_DELAY_MS = 500;

const delay = (ms: number): Promise<void> =>
    new Promise(resolve => setTimeout(resolve, ms));

/**
 * Retrieves the EVO access token.
 * When running inside an iframe (EVO platform), retries with back-off
 * so that API calls wait for the mfeHost to finish initialising.
 */
export async function getEvoAccessToken(): Promise<string | null> {
    console.log('ProjectApproval_ [EvoToken] getEvoAccessToken called — isIframeMode: %s, isEvoPlatformAvailable: %s', isIframeMode(), isEvoPlatformAvailable());

    if (!isIframeMode()) {
        console.warn('ProjectApproval_ [EvoToken] Not in iframe mode — skipping token retrieval');
        return null;
    }
    if (!isEvoPlatformAvailable()) {
        console.warn('ProjectApproval_ [EvoToken] EVO platform not available — skipping token retrieval');
        return null;
    }

    console.log('ProjectApproval_ [EvoToken] Requesting access token (max retries: %d)', MAX_TOKEN_RETRIES);

    for (let attempt = 0; attempt < MAX_TOKEN_RETRIES; attempt++) {
        try {
            const token = await getAccessToken();
            if (token) {
                console.log('ProjectApproval_ [EvoToken] Token obtained on attempt %d', attempt + 1);
                return token;
            }
            // Token was null/empty — mfeHost may not have initialised yet, retry
            console.log('ProjectApproval_ [EvoToken] Attempt %d returned empty token, retrying...', attempt + 1);
            if (attempt < MAX_TOKEN_RETRIES - 1) {
                await delay(RETRY_DELAY_MS * (attempt + 1));
            }
        } catch (err) {
            console.log('ProjectApproval_ [EvoToken] Attempt %d threw error: %o', attempt + 1, err);
            if (attempt < MAX_TOKEN_RETRIES - 1) {
                await delay(RETRY_DELAY_MS * (attempt + 1));
            }
        }
    }

    console.warn('ProjectApproval_ [EvoToken] Failed to get access token after %d retries', MAX_TOKEN_RETRIES);
    return null;
}
